<script>
import { componentStore } from "@common/componentStore/componentStore";
import { configStore } from "@common/configStore";
import defaultContactImage from "./user-default.jpg";
import { defineAsyncComponent } from "@common/defineAsyncComponent";

const editorSettings = {
    contactCardMode: {
        type: 'dropdown',
        items: [
            { text: 'Avatar Only', value: 'avatar_only' },
            { text: 'Normal', value: 'normal' },
        ],
        fields: { text: 'text', value: 'value' },
        label: 'Contact Card Mode',
        name: 'contactCardMode',
        helpText: 'Specify the mode: "Avatar Only" shows just an image. Normal shows all basic contact info. Clicking on the card in any mode will expand to show all contact info.'
    },
    contactIdSource: {
        label: 'Contact ID Source',
        name: 'contactIdSource',
        type: 'dropdown',
        items: [
            { text: 'From Prop', value: 'fromProp' },
            { text: 'Context Vol', value: 'volId' },
            { text: 'Context Opp', value: 'oppId' },
            { text: 'Context Org', value: 'orgId' },
        ],
        helpText: 'The contact ID is the record you want to pull contact information from.'
    },
    avatarImageSource: {
        label: 'Avatar Image Source',
        type: 'dropdown',
        name: 'avatarImageSource',
        items: [
            { text: 'default', value: 'default' },
            { text: 'From Props', value: 'fromProps' },
            { text: 'Specified', value: 'specified' },
        ],
        fields: { text: 'text', value: 'value' },
        helpText: 'You can specify a field or, choose default to use the "Profile Image" attachment. Choose from props to have another component specify the image source (use from params for the "View Profile" action).'
    },
    imageField: {
        label: 'Avatar Image',
        name: 'imageField',
        type: 'dropdowntree',
        fields: {
            text: 'text',
            value: 'value',
            hasChildren: 'hasChild',
            parentValue: 'pId',
        },
        itemsSource: 'configStore.allFieldOptions()',
        itemsFilter: 'this.item.type === "ATTACHMENT"',
        allowFiltering: true,
        filterType: 'Contains',
        default: [],
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.avatarImageSource === "specified"'
            }
        ],
        message: 'When specifying a field, use URL fields like: "Volunteer Badge URL".',
        helpText: 'Specify the image field you want to use.'
    }
};

const contactCardConfig = {
    cId: "contactCard",
    cType: "contactCard",
    cTitle: "Contact Card",
    cTemplate: `<div class="contact-card">
        <sam-tooltip v-if="computedContactId"
            position="RightCenter"
            @update:beforeClose="this.tooltipLoaded = false"
            @update:afterOpen="this.tooltipLoaded = true"
            css-class="contact-tooltip"
            :teleport-id="contactCardContentId"
        >
            <img :src="contactImage" :alt="contactData.recordTitle || contactFullName" class="contact-image" :title="contactData.recordTitle || contactFullName"/>
        </sam-tooltip>
        <div v-if="config.contactCardMode !== 'avatar_only'" :id="contactCardContentId"></div>
        <Teleport v-if="tooltipLoaded || contentContainerReady" :to="'#' + contactCardContentId">
            <div class="contact-card-inner" :cid="cId">
                <div class="contact-details">
                    <div class="contact-record-title" v-if="contactData.recordTitle"> {{contactData.recordTitle}}</div>
                    <div class="contact-person-name" v-if="contactFullName">
                        {{contactFullName}}<span v-if="contactData.title" class="contact-person-title">({{contactData.title}})</span>
                    </div>
                    <div class="contact-tab-contents">
                        <div v-show="view === 'main'">
                            <div class="contact-emails" v-if="contactData.emails && contactData.emails.length">
                                <div class="contact-item">
                                    <span class="fa fa-light fa-envelope leading-icon" title="phone">{{}}</span>
                                    <span class="contact-item-text"><a @click="sendEmail(contactData.emails[0].value);" title="Send Email">{{contactData.emails[0].value}}</a></span>
                                    <span class="fa fa-light fa-copy contact-item-action" @click="copyItem(contactData.emails[0].value, 'Email address')"></span>
                                    <span class="fa fa-chevron-down contact-item-action" @click="showEmails = !showEmails"></span>
                                    <div v-if="showEmails">
                                        <div class="contact-list-item" v-for="(email, emailIdx) in contactData.emails" :key="emailIdx">
                                            <span class="fa fa-light fa-envelope leading-icon" title="phone">{{}}</span>
                                            <span class="contact-item-text"><a @click="sendEmail(email.value);" title="Send Email">{{email.label}}: {{email.value}}</a></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="contact-phones" v-if="contactData.phones && contactData.phones.length">
                                <div class="contact-item">
                                    <span class="fa fa-light fa-phone leading-icon" title="phone">{{}}</span>
                                    <span class="contact-item-text"><a :href="'tel:' + contactData.phones[0].value">{{contactData.phones[0].value}}</a></span>
                                    <span class="fa fa-light fa-copy contact-item-action" @click="copyItem(contactData.phones[0].value, 'Phone Number')"></span>
                                    <span class="fa fa-chevron-down contact-item-action" @click="showPhones = !showPhones"></span>
                                    <div v-if="showPhones">
                                        <div class="contact-list-item" v-for="(phone, phoneIdx) in contactData.phones" :key="phoneIdx">
                                            <span class="fa fa-light fa-phone leading-icon" title="phone">{{}}</span>
                                            <span class="contact-item-text"><a :href="'tel:' + phone">{{phone.label}}: {{phone.value}}</a></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="contact-websites" v-if="contactData.websites && contactData.websites.length">
                                <span class="fa fa-light fa-link leading-icon">{{}}</span>
                                <span class="contact-item-text">{{contactData.websites[0].value}}</span>
                                <span class="fa fa-light fa-copy contact-item-action" @click="copyItem(contactData.websites[0].value, 'Website')"></span>
                                <span class="fa fa-chevron-down contact-item-action" @click="showWebsites = !showWebsites"></span>
                                <div class="contact-item">
                                    <div class="contact-list-item" v-for="(website, addrIdx) in contactData.websites" :key="addrIdx">
                                        <span class="fa fa-location-dot leading-icon" title="Website">{{}}</span>
                                        {{website}}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-show="view === 'addresses'">
                            <div class="contact-list-item address-item" v-for="(address, addrIdx) in contactData.addresses" :key="addrIdx">
                                <span class="fa fa-location-dot leading-icon" title="Address">{{}}</span>
                                <div class="address-info">
                                    <span v-if="address.label" class="address-label">{{address.label}}</span>
                                    <span v-if="address.address1" class="address-street address-field">{{address.address1}} {{address.address2}}</span>
                                    <span v-if="address.city" class="address-city address-field">{{address.city}}</span>
                                    <span v-if="address.state" class="address-state address-field">{{address.state}}</span>
                                    <span v-if="address.zipcode" class="address-zipcode address-field">{{address.zipcode}}</span>
                                    <span v-if="address.country" class="address-country address-field">{{address.country}}</span>
                                </div>
                            </div>
                        </div>
                        <div v-show="view === 'groups'">No groups to display.</div>
                        <div v-show="view === 'schedule'">No schedule shifts to display.</div>
                        <div v-show="view === 'other'">No other contacts to display.</div>
                    </div>
                    <div class="contact-tabs">
                        <span class="fa fa-light fa-user-astronaut contact-tab-item" title="Primary Contact Info" @click="view = 'main'"></span>
                        <span class="fa fa-location-dot contact-tab-item" title="Addresses" @click="view = 'addresses'" v-if="contactData.addresses && contactData.addresses.length"></span>
                        <span class="fa-regular fa-calendar-day contact-tab-item" title="Schedule" @click="view = 'schedule'" v-if="contactData.shifts && contactData.shifts.length"></span>
                        <span class="fa-light fa-people-group contact-tab-item" title="Groups" @click="view = 'groups'"></span>
                        <span class="fa-solid fa-people-pulling contact-tab-item" title="Other People" @click="view = 'other'"></span>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>`,
    controls: []
};

const contactCardCmp = {
    name: "contactCard",
    editorSettings: editorSettings,
    data() {
        return {
            uniqueId: library.getUniqueID(),
            view: 'main',
            showEmails: false,
            showPhones: false,
            tooltipLoaded: false,
            contentContainerReady: false
        };
    },
    props: {
        contactId: {
            default: false
        },
        contactPsp: {
            default: false
        },
        imageSourceField: {
            default: false
        },
        viewMode: {
            default: 'default'
        }
    },
    computed: {
        computedContactId() {
            if (this.config.contactIdSource === 'fromProp') return this.contactId;
            return this[this.config.contactIdSource];
        },
        contactCardContentId() {
            return 'contact_card_content_' + this.uniqueId;
        },
        contactFullName() {
            let cData = this.contactData;
            if (!cData.fullName && !cData.firstName && !cData.lastName) return false;
            if (cData.fullName) return cData.fullName;
            let output = '';
            if (cData.firstName) output = cData.firstName;
            if (cData.lastName) output += cData.lastName;
            return output;
        },
        contactData() {
            // Example output
            // {
            //     firstName: 'Tony',
            //     lastName: 'Tiger',
            //     title: 'Volunteer',
            //     image: 'sds url',
            //     emails: [{ label: 'primary', email: 'no-reply@samaritan.com' }],
            //     phones: [{ label: 'primary', phone: '555-555-5555' }],
            //     addresses: [
            //         {
            //             address1: '265 East 300 South',
            //             address2: 'Suite 265',
            //             city: 'Salt Lake City',
            //             stateAbbr: 'UT',
            //             state: 'Utah',
            //             zipcode: '84101',
            //         }
            //     ],
            //     otherPeople: [{}]
            // };
            let recordId = this.computedContactId;
            let recordPsp = this.contactPsp || 'vol';
            if (!recordId || !recordPsp) return {};
            let source = ['er_' + recordPsp + 'Fields'];
            let fieldsMap;
            // Set the image field
            let imageField = {};
            let imgKey = 'ATTACHURL#Profile Image'; // default image source
            if (this.config.avatarImageSource === 'fromProps' && this.imageSourceField) imgKey = this.imageSourceField;
            else if (this.config.avatarImageSource === 'specified') imgKey = this.config.imageField;
            if (Array.isArray(imgKey)) imgKey = imgKey[0];
            if (imgKey.indexOf(':::') !== -1) imgKey = imgKey.split(':::')[1];
            imageField[imgKey] = { type: 'image', key: 'image' };
            if (!this.tooltipLoaded && this.config.contactCardMode !== 'normal') {
                // Get minimum fields until the contactCard is opened
                fieldsMap = {
                    vol: {
                        'VOL_FIRST_NAME': { type: 'name', key: 'firstName' },
                        'VOL_LAST_NAME': { type: 'name', key: 'lastName' },
                        ...imageField
                    },
                    opp: {
                        'OPP_TITLE': { type: 'recordTitle', key: 'recordTitle' },
                        ...imageField
                    },
                    org: {
                        'ORG_NAME': { type: 'recordTitle', key: 'recordTitle' },
                        ...imageField
                    },
                };
            }
            else {
                fieldsMap = {
                    // Fields are in order of priority
                    // ie: if two email fields are filled the first will be the 'primary'
                    // Duplicate values will be removed from the output
                    vol: {
                        'VOL_FIRST_NAME': { type: 'name', key: 'firstName' },
                        'VOL_LAST_NAME': { type: 'name', key: 'lastName' },
                        'VOL_ADDRESS_1': { type: 'addresses', index: 0, key: 'address1' },
                        'VOL_ADDRESS_2': { type: 'addresses', index: 0, key: 'address2' },
                        'VOL_CITY': { type: 'addresses', index: 0, key: 'city' },
                        'VOL_STATE': { type: 'addresses', index: 0, key: 'state' },
                        'VOL_POSTAL_CODE': { type: 'addresses', index: 0, key: 'zipcode' },
                        'VOL_CONTACT#4': { type: 'phones', key: 'phone' },
                        'VOL_CONTACT#7': { type: 'emails', key: 'email' },
                        'VOL_USER_ID': { type: 'emails', key: 'email' },
                        ...imageField
                    },
                    opp: {
                        'OPP_TITLE': { type: 'recordTitle', key: 'recordTitle' },
                        'OPP_LOC_PHONE': { type: 'phones', key: 'phone' },
                        'OPP_ADDRESS_1': { type: 'addresses', index: 0, key: 'address1' },
                        'OPP_ADDRESS_2': { type: 'addresses', index: 0, key: 'address2' },
                        'OPP_CITY': { type: 'addresses', index: 0, key: 'city' },
                        'OPP_STATE': { type: 'addresses', index: 0, key: 'state' },
                        'OPP_COUNTRY': { type: 'addresses', index: 0, key: 'country' },
                        'OPP_POSTAL_CODE': { type: 'addresses', index: 0, key: 'zipcode' },
                        'OPP_LOC_ADDRESS_1': { type: 'addresses', index: 1, key: 'address1' },
                        'OPP_LOC_ADDRESS_2': { type: 'addresses', index: 1, key: 'address2' },
                        'OPP_LOC_CITY': { type: 'addresses', index: 1, key: 'city' },
                        'OPP_LOC_STATE': { type: 'addresses', index: 1, key: 'state' },
                        'OPP_LOC_COUNTY_ID': { type: 'addresses', index: 1, key: 'county' },
                        'OPP_LOC_POSTAL_CODE': { type: 'addresses', index: 1, key: 'zipcode' },
                        'OPP_LOC_COUNTRY': { type: 'addresses', index: 1, key: 'country' },
                        'OPP_URL': { type: 'websites', key: 'website' },
                        // TODO connect this to the contact fields in the system
                        'OPP_CONTACT#14': { type: 'name', key: 'fullName' }, // fullName is broken up into firstName lastName by the mapper
                        'OPP_CONTACT#15': { type: 'title', key: 'title' },
                        'OPP_CONTACT#4': { type: 'phones', key: 'phone', label: 'Primary' },
                        'OPP_CONTACT#5': { type: 'phones', key: 'phone', label: 'Secondary' },
                        'OPP_CONTACT#8': { type: 'phones', key: 'phone', label: 'Other' },
                        'OPP_CONTACT#6': { type: 'phones', key: 'phone', label: 'Fax' },
                        'OPP_CONTACT#7': { type: 'emails', key: 'email' },
                        ...imageField
                    },
                    org: {
                        'ORG_NAME': { type: 'recordTitle', key: 'recordTitle' },
                        'ORG_ADDRESS_1': { type: 'addresses', index: 0, key: 'address1' },
                        'ORG_ADDRESS_2': { type: 'addresses', index: 0, key: 'address2' },
                        'ORG_CITY': { type: 'addresses', index: 0, key: 'city' },
                        'ORG_STATE': { type: 'addresses', index: 0, key: 'state' },
                        'ORG_COUNTRY': { type: 'addresses', index: 0, key: 'country' },
                        'ORG_POSTAL_CODE': { type: 'addresses', index: 0, key: 'zipcode' },
                        'ORG_URL': { type: 'websites', key: 'website' },
                        // TODO connect this to the contact fields in the system
                        'ORG_CONTACT#14': { type: 'name', key: 'fullName' },
                        'ORG_CONTACT#15': { type: 'title', key: 'title' },
                        'ORG_CONTACT#4': { type: 'phones', key: 'phone', label: 'Primary' },
                        'ORG_CONTACT#5': { type: 'phones', key: 'phone', label: 'Secondary' },
                        'ORG_CONTACT#8': { type: 'phones', key: 'phone', label: 'Other' },
                        'ORG_CONTACT#6': { type: 'phones', key: 'phone', label: 'Fax' },
                        'ORG_CONTACT#7': { type: 'emails', key: 'email' },
                        ...imageField
                    }
                };
            }
            let fields = (typeof fieldsMap[recordPsp] === 'object' && Object.keys(fieldsMap[recordPsp])) || {};
            let payload = {
                source, params: { recordId, fields }
            };
            let subscription = this.recordStore.subscribe(payload, this.cId, false, false);
            let records = this.recordStore.getSubscription(subscription.key);
            let mapFields = (record) => {
                let output = {};
                let rFields = typeof record !== 'object' ? [] : Object.keys(record);
                if (!rFields.length) return output;
                rFields.forEach((field) => {
                    let oVal = record[field];
                    let fField = fieldsMap[recordPsp][field];
                    // Don't use fields from the record that aren't in the map
                    // Don't process empty values
                    if (!fField || !oVal) return;
                    let oKey = fField.key;
                    let oType = fField.type;
                    if (oType === 'addresses') {
                        let index = fField.index;
                        if (!output[oType]) output[oType] = [];
                        if (!output[oType][index]) output[oType][index] = {};
                        if (!output[oType][index][oKey]) output[oType][index][oKey] = oVal;
                    }
                    else if (oType === 'emails' || oType === 'phones') {
                        if (!output[oType]) output[oType] = [];
                        let addItem = true;
                        output[oType].forEach((iObj) => {
                            if (iObj.value === oVal) addItem = false;
                        });
                        if (addItem) output[oType].push({ label: 'label', value: oVal });
                    } else if (oType === 'fullName') {
                        let nParts = oVal.split(' ');
                        output.firstName = nParts[0];
                        output.lastName = nParts[1];
                    }
                    else {
                        output[oKey] = oVal;
                    }
                });
                let isOpp = rFields.find((field) => field.indexOf('OPP_') === 0);
                if (Array.isArray(output.addresses)) {
                    if (output.addresses[0] && Object.keys(output.addresses[0]).length) {
                        output.addresses[0].label = isOpp ? 'Contact Address' : 'Primary';
                    } else delete output.addresses[0];
                    if (output.addresses[1] && Object.keys(output.addresses[1]).length) {
                        output.addresses[1].label = isOpp ? 'Location Address' : 'Secondary';
                    } else delete output.addresses[1];
                }
                return output;
            };
            return mapFields(records[this.computedContactId]);
        },
        contactImage() {
            let imageUrl = this.contactData.image;
            if (!imageUrl) return defaultContactImage;
            if (typeof VUE_PROJECT_NAME !== 'undefined'
                && VUE_PROJECT_NAME === 'devDash'
                && this.configStore.devDashLocalInstanceDetails
                && this.configStore.devDashLocalInstanceDetails.url
                && imageUrl.indexOf(this.configStore.devDashLocalInstanceDetails.url) !== -1
            ) {
                imageUrl = imageUrl.replace(this.configStore.devDashLocalInstanceDetails.url, PROJECTS_URL);
                imageUrl += '&isDevDash=1';
            }
            return imageUrl;
        }
    },
    methods: {
        copyItem(text, type) {
            library.copyTextToClipboard(text);
            library.showToast(type + ' copied to clipboard.');
        },
        sendEmail() {
            // actionStore.openEmail();
        },
        onLoad() {
            configStore.getDevDashLocalInstanceDetails();
            if (this?.config?.contactCardMode !== 'avatar_only') {
                setTimeout(() => {
                    this.contentContainerReady = true;
                }, 200); // NOSONAR
            }
        }
    },
    components: {
        'sam-tooltip': defineAsyncComponent(() => import('@common/control/samTooltip')),
    }
};
componentStore.initComponent(contactCardCmp, contactCardConfig);

export default contactCardCmp;

</script>
<style>
.e-tooltip-wrap.e-popup.contact-tooltip {
    display: flex;
    padding: 10px;
    border: 1px solid #eee;
    border-radius: 5px;
    box-shadow: 3px 3px 10px rgba(0,0,0,0.3);
    background: white;
    max-width: 600px;
}
.e-tooltip-wrap.contact-tooltip .e-arrow-tip-inner, .e-tooltip-wrap.contact-tooltip .e-arrow-tip-outer {
    display: none;
}
.contact-tooltip.e-tooltip-wrap .e-tip-content {
    color: #5b5b5b;
    font-size: 16px;
}
</style>
<style scoped>
.contact-record-title {
    margin: 10px 0;
    color: grey;
    font-size: 16px;
}
.contact-person-name {
    font-size: 20px;
    margin: 10px 0;
}
.contact-person-title {
    font-size: 16px;
    color: grey;
    margin-left: 5px;
}
.contact-emails {
    margin-bottom: 6px;
}
:deep(.contact-card .e-control) {
    font-size: 16px;
}
.contact-card.avatar-only {
    display: inline-block;
}
.contact-image {
    width: 100%;
    max-width: 120px;
    max-height: 120px;
    border-radius: 50%;
    background-size: 100%;
    cursor: pointer;
}
.contact-card-inner {
    display: flex;
    width: 600px;
}
.contact-details {
    padding: 0 10px;
    font-size: 16px;
    width: 100%;
    flex-grow: 1;
}
.address-field + .address-field:before {
    content: ' ';
}
.address-street, .address-label {
    display: block;
}
.address-state + .address-zipcode:before {
    content: ', '
}
.leading-icon {
    color: #626262;
    margin-right: 10px;
}
.contact-list-item {
    padding: 5px 0;
}
.contact-list-item + .contact-list-item {
    border-top: 1px solid #ddd;
}
.contact-item-action {
    cursor: pointer;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    line-height: 28px;
    text-align: center;
    background: white;
    opacity: 0;
    color: #929ba9;
}
.contact-item:hover .contact-item-action {
    opacity: 1;
}
.contact-item-action:hover {
    background: #c2e0d8;
}
.contact-tabs {
    display: flex;
    border-top: 1px solid #ddd;
    margin-top: 5px;
    padding-top: 5px;
}
.contact-tabs .contact-tab-item {
    background: transparent;
    height: 50px;
    width: 50px;
    margin: 0 10px;
    font-size: 30px;
    color: #3565a1;
    opacity: 1;
    border-radius: 50%;
    text-align: center;
    line-height: 50px; /* Vertical Center */
    cursor: pointer;
}
.contact-tabs .contact-tab-item:hover {
    background: #f1eacc;
}
.contact-list-item + .contact-list-item {
    margin: 0 10px;
    padding: 3px 0;
    border-top: 1px solid #eee;
}
.address-item {
    display: flex;
}
.address-info {
    flex-grow: 1;
}
.contact-tab-contents > div {
    min-height: 100px;
}
</style>
